// ═══════════════════════════════════════════════════════════════════════════
// PanneauInstances.jsx — Instances / Tenants — PesacoreIH
// ═══════════════════════════════════════════════════════════════════════════

import {
  AlertTriangle, Building2, CheckCircle, ChevronRight,
  Crown,
  Layers, Loader, LogIn, Plus,
  RefreshCw, Search, Shield, Trash2, Users, X
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import api from '../../config/axiosConfig';
import usePesacoreIH from '../../stores/usePesacore';

// ─── Config ───────────────────────────────────────────────────────────────

const PLANS = ['freemium', 'decouverte', 'parcours', 'praticien', 'academia', 'pro', 'entreprise'];

const PLAN_CONFIG = {
  freemium:   { color: 'text-zinc-300',    bg: 'bg-zinc-800/60',        border: 'border-zinc-700/40'    },
  decouverte: { color: 'text-slate-300',   bg: 'bg-slate-800/60',       border: 'border-slate-500/40'    },
  parcours:   { color: 'text-slate-200',   bg: 'bg-slate-800/60',       border: 'border-slate-500/40'    },
  praticien:  { color: 'text-slate-200',   bg: 'bg-slate-800/60',       border: 'border-slate-500/40'    },
  academia:   { color: 'text-sky-300',     bg: 'bg-sky-950/30',         border: 'border-sky-700/40'     },
  pro:        { color: 'text-orange-300',  bg: 'bg-orange-950/30',      border: 'border-orange-700/40'  },
  entreprise: { color: 'text-purple-300',  bg: 'bg-purple-950/30',      border: 'border-purple-700/40'  },
};

const STATUS_CONFIG = {
  active:    { label: 'Actif',     color: 'text-emerald-400', dot: 'bg-emerald-500' },
  suspended: { label: 'Suspendu',  color: 'text-amber-400',   dot: 'bg-amber-500'   },
  inactive:  { label: 'Inactif',   color: 'text-zinc-500',    dot: 'bg-zinc-600'    },
};

// ─── Composants UI ────────────────────────────────────────────────────────

function PlanBadge({ plan }) {
  const cfg = PLAN_CONFIG[plan] || PLAN_CONFIG.freemium;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded border text-[10px] font-mono ${cfg.bg} ${cfg.border} ${cfg.color}`}>
      {plan}
    </span>
  );
}

function StatusDot({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.active;
  return (
    <span className="flex items-center gap-1.5">
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot} animate-pulse`} />
      <span className={`text-[10px] uppercase tracking-wider ${cfg.color}`}>{cfg.label}</span>
    </span>
  );
}

function QuotaBar({ label, used, limit, unlimited }) {
  const pct   = unlimited || limit <= 0 ? 0 : Math.min(100, Math.round((used / limit) * 100));
  const color = pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-amber-500' : 'bg-emerald-500';
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-[10px] text-zinc-500">
        <span>{label}</span>
        <span className={pct >= 90 ? 'text-red-400' : pct >= 70 ? 'text-amber-400' : 'text-zinc-400'}>
          {unlimited ? `${used} / ∞` : `${used} / ${limit}`}
        </span>
      </div>
      <div className="h-1 bg-zinc-800 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-700 ${unlimited ? 'bg-zinc-700' : color}`}
          style={{ width: unlimited ? '100%' : `${pct}%`, opacity: unlimited ? 0.3 : 1 }} />
      </div>
    </div>
  );
}

function Feedback({ msg, type, onClear }) {
  if (!msg) return null;
  const s = {
    success: 'bg-emerald-950/30 border-emerald-800/30 text-emerald-300',
    error:   'bg-red-950/30 border-red-800/30 text-red-300',
    warning: 'bg-amber-950/30 border-amber-800/30 text-amber-300',
    info:    'bg-sky-950/30 border-sky-800/30 text-sky-300',
  };
  return (
    <div className={`flex items-center justify-between px-4 py-2.5 rounded border text-xs ${s[type] || s.info}`}>
      <span>{msg}</span>
      {onClear && <button onClick={onClear}><X size={12} /></button>}
    </div>
  );
}

// ─── Hook données ─────────────────────────────────────────────────────────

function useInstancesData() {
  // ── Connexion store/moteur ─────────────────────────────────────────────
  const {
    instances:     storeInstances,
    loadInstances: storeLoad,
    suspendInstance: storeSuspend,
    checkInstanceQuota,
    prepareAction,
  } = usePesacoreIH();

  const [instances,  setInstances]  = useState([]);
  const [summary,    setSummary]    = useState(null);
  const [pagination, setPagination] = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState(null);

  const fetchAll = async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const query = new URLSearchParams(params).toString();
      const r     = await api.get(`/superadmin/instances${query ? '?' + query : ''}`);
      setInstances(r.data.instances   || []);
      setSummary(r.data.summary       || null);
      setPagination(r.data.pagination || null);
      // Sync le store aussi (pour le badge pendingChanges header)
      storeLoad?.();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (tenantId, status, reason) => {
    // Pour suspend : passer par le store qui valide via le moteur
    if (status === 'suspended') {
      const result = await storeSuspend?.(tenantId);
      if (result && !result.success) throw new Error(result.error || result.errors?.join(', '));
    } else {
      await api.patch(`/superadmin/instances/${tenantId}/status`, { status, reason });
    }
    setInstances(prev => prev.map(i => i._id === tenantId ? { ...i, status } : i));
    return { success: true };
  };

  const updatePlan = async (tenantId, plan) => {
    // Validation quota moteur avant changement de plan 
    const instance = instances.find(i => i._id === tenantId);
    if (instance && checkInstanceQuota) {
      const quotaCheck = checkInstanceQuota(plan, 'learners', instance.stats?.userCount || 0);
      if (quotaCheck && !quotaCheck.valid) throw new Error(`Quota dépassé pour le plan ${plan}`);
    }
    const r = await api.patch(`/superadmin/instances/${tenantId}/plan`, { plan });
    setInstances(prev => prev.map(i => i._id === tenantId
      ? { ...i, plan, subscription: { ...i.subscription, plan } } : i
    ));
    return r.data;
  };

  const createInstance = async (data) => {
    // Validation moteur
    const check = prepareAction?.('INSTANCE_CREATE', data);
    if (check && !check.valid) throw new Error(check.errors?.join(', ') || 'Rejeté par le moteur');
    const r = await api.post('/superadmin/instances', data);
    await fetchAll();
    return r.data;
  };

  const impersonate = async (tenantId) => {
    const r = await api.post(`/superadmin/instances/${tenantId}/impersonate`);
    return r.data;
  };

  const deleteInstance = async (tenantId, confirmationToken) => {
    const r = await api.delete(`/superadmin/instances/${tenantId}`, { data: { confirmationToken } });
    setInstances(prev => prev.filter(i => i._id !== tenantId));
    return r.data;
  };

  useEffect(() => { fetchAll(); }, []);

  return {
    instances, summary, pagination, loading, error,
    fetchAll, updateStatus, updatePlan,
    createInstance, impersonate, deleteInstance,
    checkInstanceQuota,
  };
}

// ─── Drawer détail tenant ─────────────────────────────────────────────────

function DrawerDetail({ tenantId, onClose, onUpdateStatus, onUpdatePlan, onImpersonate }) {
  const [data,     setData]     = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [feedback, setFeedback] = useState(null);
  const [newPlan,  setNewPlan]  = useState('');
  const [reason,   setReason]   = useState('');
  const [delToken, setDelToken] = useState('');
  const [showDel,  setShowDel]  = useState(false);
  const { fetchDetail, deleteInstance } = useInstancesData();

  const showFb = (type, msg) => { setFeedback({ type, msg }); setTimeout(() => setFeedback(null), 5000); };

  useEffect(() => {
    api.get(`/superadmin/instances/${tenantId}`)
      .then(r => { setData(r.data); setNewPlan(r.data.tenant?.plan || 'freemium'); })
      .catch(err => showFb('error', err.response?.data?.error || err.message))
      .finally(() => setLoading(false));
  }, [tenantId]);

  const handleImpersonate = async () => {
    try {
      const r = await onImpersonate(tenantId);
      showFb('warning', `Token impersonation généré (15 min) — copié dans le presse-papier`);
      navigator.clipboard.writeText(r.token).catch(() => {});
    } catch (err) {
      showFb('error', err.response?.data?.error || err.message);
    }
  };

  const handlePlanChange = async () => {
    try {
      await onUpdatePlan(tenantId, newPlan);
      showFb('success', `Plan changé → ${newPlan}`);
      setData(prev => prev ? { 
        ...prev, 
        tenant: { ...prev.tenant, plan: newPlan }  // ← champ direct
      } : prev);
    } catch (err) {
      showFb('error', err.response?.data?.error || err.message);
    }
  };

  const handleSuspend = async () => {
    try {
      const isSuspended = data?.tenant?.status === 'suspended';
      await onUpdateStatus(tenantId, isSuspended ? 'active' : 'suspended', reason);
      showFb('success', isSuspended ? 'Tenant réactivé' : 'Tenant suspendu');
      setData(prev => prev ? { ...prev, tenant: { ...prev.tenant, status: isSuspended ? 'active' : 'suspended' } } : prev);
    } catch (err) {
      showFb('error', err.response?.data?.error || err.message);
    }
  };

  if (loading) return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center">
      <Loader size={24} className="animate-spin text-orange-400" />
    </div>
  );

  const { tenant, quotaUsage, admins = [] } = data || {};
  if (!tenant) return null;

  const isSuspended = tenant.status === 'suspended';
  const plan        = tenant.plan || 'freemium';
  const expectedDel = `DELETE_${tenant.slug?.toUpperCase().slice(0, 20)}`;

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex justify-end" onClick={onClose}>
      <div className="w-full max-w-lg bg-zinc-950 border-l border-zinc-800 overflow-y-auto"
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="sticky top-0 bg-zinc-950 border-b border-zinc-800/60 px-5 py-4 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Building2 size={14} className="text-orange-400" />
              <h3 className="text-sm font-black text-zinc-100">{tenant.name}</h3>
              <PlanBadge plan={plan} />
            </div>
            <p className="text-[10px] text-zinc-600 mt-0.5">{tenant.slug} · {tenant.contactEmail}</p>
          </div>
          <button onClick={onClose} className="p-1.5 text-zinc-600 hover:text-white">
            <X size={16} />
          </button>
        </div>

        <div className="p-5 space-y-5">
          <Feedback {...(feedback || {})} onClear={() => setFeedback(null)} />

          {/* Status & Actions rapides */}
          <div className="flex items-center gap-3 flex-wrap">
            <StatusDot status={tenant.status} />
            <button onClick={handleSuspend}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded border text-xs ${
                isSuspended
                  ? 'bg-emerald-950/30 border-emerald-700/40 text-emerald-300 hover:bg-emerald-950/50'
                  : 'bg-amber-950/30 border-amber-700/40 text-amber-300 hover:bg-amber-950/50'
              }`}>
              {isSuspended ? <CheckCircle size={12} /> : <Shield size={12} />}
              {isSuspended ? 'Réactiver' : 'Suspendre'}
            </button>
            <button onClick={handleImpersonate}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-950/30 border border-purple-700/40 rounded text-purple-300 text-xs hover:bg-purple-950/50">
              <LogIn size={12} />
              Impersonation
            </button>
          </div>

          {isSuspended && (
            <div>
              <label className="text-[10px] text-zinc-500 uppercase tracking-widest block mb-1">Raison suspension</label>
              <input value={reason} onChange={e => setReason(e.target.value)}
                className="w-full bg-black border border-zinc-700 rounded px-3 py-2 text-xs text-zinc-200 outline-none"
                placeholder="Optionnel — visible dans les logs" />
            </div>
          )}

          {/* Quotas */}
          {quotaUsage && (
            <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-4 space-y-3">
              <p className="text-[10px] text-zinc-600 uppercase tracking-widest">Quotas consommés</p>
              <QuotaBar label="Utilisateurs" used={quotaUsage.learners.used} limit={quotaUsage.learners.limit} unlimited={quotaUsage.learners.unlimited} />
              <QuotaBar label="Parcours"     used={quotaUsage.parcours.used} limit={quotaUsage.parcours.limit} unlimited={quotaUsage.parcours.unlimited} />
              <QuotaBar label="Stockage MB"  used={quotaUsage.storage.used}  limit={quotaUsage.storage.limit}  unlimited={quotaUsage.storage.unlimited} />
              <div className="grid grid-cols-3 gap-2 pt-1">
                {[
                  { label: 'Users',    value: quotaUsage.userCount },
                  { label: 'Parcours', value: quotaUsage.parcoursCount },
                  { label: 'Admins',   value: quotaUsage.adminCount },
                ].map(k => (
                  <div key={k.label} className="text-center">
                    <div className="text-lg font-black text-zinc-200">{k.value}</div>
                    <div className="text-[9px] text-zinc-600 uppercase">{k.label}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Changer de plan */}
          <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-4 space-y-3">
            <p className="text-[10px] text-zinc-600 uppercase tracking-widest">Changer de plan</p>
            <div className="flex gap-2">
              {PLANS.map(p => (
                <button key={p} onClick={() => setNewPlan(p)}
                  className={`flex-1 py-2 rounded border text-[10px] font-mono transition-all ${
                    newPlan === p
                      ? `${PLAN_CONFIG[p].bg} ${PLAN_CONFIG[p].border} ${PLAN_CONFIG[p].color}`
                      : 'bg-zinc-800 border-zinc-700 text-zinc-600 hover:text-zinc-400'
                  }`}>
                  {p}
                </button>
              ))}
            </div>
            <button onClick={handlePlanChange} disabled={newPlan === plan}
              className="w-full py-2 bg-orange-600/30 hover:bg-orange-600/50 border border-orange-500/40 rounded text-orange-200 text-xs disabled:opacity-30">
              Appliquer le plan {newPlan}
            </button>
          </div>

          {/* Admins */}
          {admins.length > 0 && (
            <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl overflow-hidden">
              <div className="px-4 py-2.5 border-b border-zinc-800/40 text-[10px] uppercase tracking-widest text-zinc-600">
                Administrateurs ({admins.length})
              </div>
              {admins.map(a => (
                <div key={a._id} className="flex items-center gap-3 px-4 py-2.5 border-b border-zinc-800/20 last:border-0">
                  <div className="w-7 h-7 rounded-full bg-zinc-800 flex items-center justify-center text-[10px] text-zinc-400 font-bold">
                    {a.prenom?.[0]}{a.nom?.[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-zinc-200">{a.prenom} {a.nom}</p>
                    <p className="text-[10px] text-zinc-600">{a.email}</p>
                  </div>
                  <span className="text-[10px] text-zinc-600 font-mono">{a.role}</span>
                </div>
              ))}
            </div>
          )}

          {/* Zone danger */}
          <div className="bg-red-950/10 border border-red-900/30 rounded-xl p-4 space-y-3">
            <p className="text-[10px] text-red-500 uppercase tracking-widest flex items-center gap-1.5">
              <AlertTriangle size={11} /> Zone danger
            </p>
            {!showDel ? (
              <button onClick={() => setShowDel(true)}
                className="flex items-center gap-1.5 px-3 py-2 bg-red-950/30 border border-red-800/30 rounded text-red-300 text-xs">
                <Trash2 size={12} />
                Supprimer ce tenant
              </button>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-red-300">Tapez <span className="font-mono bg-red-950/40 px-1 rounded">{expectedDel}</span> pour confirmer</p>
                <div className="flex gap-2">
                  <input value={delToken} onChange={e => setDelToken(e.target.value)}
                    className="flex-1 bg-black border border-red-800/40 rounded px-3 py-2 text-xs font-mono text-zinc-200 outline-none"
                    placeholder={expectedDel} />
                  <button
                    onClick={async () => {
                      try {
                        await api.delete(`/superadmin/instances/${tenantId}`, { data: { confirmationToken: delToken } });
                        onClose();
                      } catch (err) {
                        showFb('error', err.response?.data?.error || err.message);
                      }
                    }}
                    disabled={delToken !== expectedDel}
                    className="px-3 py-2 bg-red-700/40 border border-red-600/40 rounded text-red-200 text-xs disabled:opacity-30">
                    Supprimer
                  </button>
                  <button onClick={() => { setShowDel(false); setDelToken(''); }}
                    className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-zinc-400 text-xs">
                    <X size={12} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Formulaire création tenant ───────────────────────────────────────────

function FormCreation({ onSubmit, onClose }) {
  const [form, setForm] = useState({
    name: '', type: 'company', plan: 'freemium',
    contactEmail: '', adminEmail: '', adminNom: '', adminPrenom: '',
  });
  const [err,     setErr]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [created, setCreated] = useState(null);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async () => {
    setErr(null);
    if (!form.name.trim())         return setErr('Nom obligatoire');
    if (!form.contactEmail.trim()) return setErr('Email de contact obligatoire');
    setLoading(true);
    try {
      const r = await onSubmit(form);
      setCreated(r);
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  if (created) return (
    <div className="bg-zinc-900/60 border border-emerald-800/30 rounded-xl p-5 space-y-3">
      <div className="flex items-center gap-2 text-emerald-300">
        <CheckCircle size={14} />
        <h4 className="text-sm font-black uppercase tracking-tighter">Tenant créé</h4>
      </div>
      <div className="text-xs text-zinc-400 space-y-1">
        <p>ID : <span className="font-mono text-zinc-200">{created.tenant?._id}</span></p>
        <p>Slug : <span className="font-mono text-zinc-200">{created.tenant?.slug}</span></p>
        {created.admin && (
          <>
            <p>Admin : <span className="font-mono text-zinc-200">{created.admin.email}</span></p>
            <p className="text-amber-300">⚠ Mot de passe temporaire : <span className="font-mono font-bold">{created.admin.tempPassword}</span></p>
          </>
        )}
      </div>
      <button onClick={onClose} className="px-4 py-2 bg-zinc-800 border border-zinc-700 rounded text-zinc-300 text-xs">Fermer</button>
    </div>
  );

  return (
    <div className="bg-zinc-900/60 border border-orange-800/30 rounded-xl p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-black text-orange-300 uppercase tracking-tighter">Nouveau Tenant</h4>
        <button onClick={onClose}><X size={14} className="text-zinc-500 hover:text-white" /></button>
      </div>
      {err && <div className="px-3 py-2 bg-red-950/30 border border-red-800/30 rounded text-red-300 text-xs">{err}</div>}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {[
          { k: 'name',         label: 'Nom organisation',  placeholder: 'Acme Corp' },
          { k: 'contactEmail', label: 'Email contact',     placeholder: 'contact@acme.com' },
          { k: 'adminEmail',   label: 'Email admin',       placeholder: 'admin@acme.com (optionnel)' },
          { k: 'adminNom',     label: 'Nom admin',         placeholder: 'Dupont' },
          { k: 'adminPrenom',  label: 'Prénom admin',      placeholder: 'Jean' },
        ].map(({ k, label, placeholder }) => (
          <div key={k}>
            <label className="text-[10px] text-zinc-500 uppercase tracking-widest block mb-1">{label}</label>
            <input value={form[k]} onChange={e => set(k, e.target.value)}
              placeholder={placeholder}
              className="w-full bg-black border border-zinc-700 rounded px-3 py-2 text-xs text-zinc-200 focus:border-orange-600/50 outline-none" />
          </div>
        ))}
        <div>
          <label className="text-[10px] text-zinc-500 uppercase tracking-widest block mb-1">Plan</label>
          <select value={form.plan} onChange={e => set('plan', e.target.value)}
            className="w-full bg-black border border-zinc-700 rounded px-3 py-2 text-xs text-zinc-200 focus:border-orange-600/50 outline-none">
            {PLANS.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] text-zinc-500 uppercase tracking-widest block mb-1">Type</label>
          <select value={form.type} onChange={e => set('type', e.target.value)}
            className="w-full bg-black border border-zinc-700 rounded px-3 py-2 text-xs text-zinc-200 focus:border-orange-600/50 outline-none">
            {['company','school','individual','system'].map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      </div>

      <div className="flex gap-3 justify-end pt-2">
        <button onClick={onClose} className="px-4 py-2 bg-zinc-800 border border-zinc-700 rounded text-zinc-300 text-xs">Annuler</button>
        <button onClick={handleSubmit} disabled={loading || !form.name.trim()}
          className="px-4 py-2 bg-orange-600/30 hover:bg-orange-600/50 border border-orange-500/40 rounded text-orange-200 text-xs disabled:opacity-30">
          {loading ? <Loader size={12} className="animate-spin" /> : 'Créer le tenant'}
        </button>
      </div>
    </div>
  );
}

// ─── Carte tenant ─────────────────────────────────────────────────────────

function TenantCard({ instance, onSelect }) {
  const plan = instance.plan || 'freemium';
  const stats = instance.stats || {};
  return (
    <div
      onClick={() => onSelect(instance._id)}
      className="bg-zinc-900/40 border border-zinc-800/60 hover:border-zinc-700/60 rounded-xl p-4 cursor-pointer transition-all group"
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="text-sm font-bold text-zinc-200 truncate">{instance.name}</h4>
            <PlanBadge plan={plan} />
          </div>
          <p className="text-[10px] text-zinc-600 truncate mt-0.5">{instance.contactEmail}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <StatusDot status={instance.status} />
          <ChevronRight size={14} className="text-zinc-700 group-hover:text-zinc-400 transition-colors" />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2">
        {[
          { icon: Users,  label: 'Users',    value: stats.userCount    ?? 0 },
          { icon: Layers, label: 'Parcours', value: stats.parcoursCount ?? 0 },
          { icon: Crown,  label: 'Admins',   value: stats.adminCount   ?? 0 },
        ].map(({ icon: Icon, label, value }) => (
          <div key={label} className="flex items-center gap-1.5">
            <Icon size={11} className="text-zinc-600 shrink-0" />
            <span className="text-xs font-bold text-zinc-300">{value}</span>
            <span className="text-[9px] text-zinc-600">{label}</span>
          </div>
        ))}
      </div>

      <p className="text-[9px] text-zinc-700 mt-2 font-mono truncate">{instance.slug}</p>
    </div>
  );
}

// ─── Panneau principal ────────────────────────────────────────────────────

export default function PanneauInstances() {
  const {
    instances, summary, pagination, loading, error,
    fetchAll, updateStatus, updatePlan, createInstance, impersonate,
  } = useInstancesData();

  const [search,   setSearch]   = useState('');
  const [planFlt,  setPlanFlt]  = useState('all');
  const [statFlt,  setStatFlt]  = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const showFb = (type, msg) => { setFeedback({ type, msg }); setTimeout(() => setFeedback(null), 4000); };

  const filtered = useMemo(() => {
    return instances.filter(i => {
      const plan = i.plan || 'freemium';
      const matchS = !search || i.name.toLowerCase().includes(search.toLowerCase()) || i.contactEmail?.includes(search.toLowerCase());
      const matchP = planFlt === 'all' || plan === planFlt;
      const matchT = statFlt === 'all' || i.status === statFlt;
      return matchS && matchP && matchT;
    });
  }, [instances, search, planFlt, statFlt]);

  return (
    <div className="space-y-5 max-w-5xl mx-auto">

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-black text-orange-300 tracking-tighter">INSTANCES</h2>
          {summary && (
            <p className="text-[10px] text-zinc-600 uppercase tracking-widest mt-0.5">
              {summary.total} tenants · {summary.active} actifs · {summary.suspended} suspendus
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => fetchAll()} disabled={loading}
            className="p-2 bg-zinc-900 border border-zinc-700 rounded text-zinc-500 hover:text-zinc-300 disabled:opacity-40">
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
          </button>
          <button onClick={() => setShowForm(v => !v)}
            className="flex items-center gap-1.5 px-3 py-2 bg-zinc-900 border border-zinc-700 hover:border-orange-600/40 rounded text-zinc-300 text-xs">
            <Plus size={12} />
            Nouveau tenant
          </button>
        </div>
      </div>

      {/* KPIs */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {PLANS.map(p => (
            <div key={p} className={`rounded-xl border p-3 text-center ${PLAN_CONFIG[p].bg} ${PLAN_CONFIG[p].border}`}>
              <div className={`text-2xl font-black ${PLAN_CONFIG[p].color}`}>{summary.byPlan?.[p] || 0}</div>
              <div className="text-[9px] text-zinc-600 uppercase tracking-widest mt-0.5">{p}</div>
            </div>
          ))}
        </div>
      )}

      {error && (
        <div className="px-4 py-3 bg-red-950/30 border border-red-800/30 rounded-xl text-red-300 text-xs flex gap-2">
          <AlertTriangle size={14} />{error}
        </div>
      )}

      <Feedback {...(feedback || {})} onClear={() => setFeedback(null)} />

      {showForm && (
        <FormCreation
          onSubmit={async (data) => { const r = await createInstance(data); showFb('success', `Tenant "${data.name}" créé`); return r; }}
          onClose={() => setShowForm(false)}
        />
      )}

      {/* Filtres */}
      <div className="flex gap-2 flex-wrap items-center">
        <div className="relative flex-1 min-w-40">
          <Search size={11} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher un tenant..."
            className="w-full pl-8 pr-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-xs text-zinc-200 focus:border-orange-600/50 outline-none" />
        </div>
        <div className="flex gap-1">
          {['all', ...PLANS].map(p => (
            <button key={p} onClick={() => setPlanFlt(p)}
              className={`px-2.5 py-1.5 rounded border text-[10px] font-mono transition-all ${
                planFlt === p
                  ? p === 'all' ? 'bg-zinc-700 border-zinc-600 text-zinc-200' : `${PLAN_CONFIG[p]?.bg} ${PLAN_CONFIG[p]?.border} ${PLAN_CONFIG[p]?.color}`
                  : 'bg-zinc-900 border-zinc-800 text-zinc-600 hover:text-zinc-400'
              }`}>
              {p === 'all' ? 'Tous' : p}
            </button>
          ))}
        </div>
        <div className="flex gap-1">
          {['all', 'active', 'suspended'].map(s => (
            <button key={s} onClick={() => setStatFlt(s)}
              className={`px-2.5 py-1.5 rounded border text-[10px] transition-all ${
                statFlt === s ? 'bg-zinc-700 border-zinc-600 text-zinc-200' : 'bg-zinc-900 border-zinc-800 text-zinc-600 hover:text-zinc-400'
              }`}>
              {s === 'all' ? 'Tous statuts' : STATUS_CONFIG[s]?.label || s}
            </button>
          ))}
        </div>
      </div>

      {/* Grille tenants */}
      {loading && instances.length === 0 ? (
        <div className="text-center py-12 text-zinc-700 text-sm">Chargement...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 border border-zinc-800/40 rounded-xl text-zinc-700 text-sm">
          Aucun tenant trouvé
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {filtered.map(i => (
            <TenantCard key={i._id} instance={i} onSelect={setSelected} />
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.pages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(p => (
            <button key={p} onClick={() => fetchAll({ page: p })}
              className={`w-8 h-8 rounded border text-xs ${
                pagination.page === p ? 'bg-orange-950/40 border-orange-700/50 text-orange-300' : 'bg-zinc-900 border-zinc-800 text-zinc-600'
              }`}>
              {p}
            </button>
          ))}
        </div>
      )}

      {/* Drawer détail */}
      {selected && (
        <DrawerDetail
          tenantId={selected}
          onClose={() => setSelected(null)}
          onUpdateStatus={updateStatus}
          onUpdatePlan={updatePlan}
          onImpersonate={impersonate}
        />
      )}
    </div>
  );
}